import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, User, Menu, X, Globe, LayoutDashboard } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";

export const Logo = ({ light }) => (
  <Link to="/" data-testid="header-logo" className={`font-display text-2xl font-extrabold tracking-tight ${light ? "text-white" : "text-[#0B0D0F]"}`}>
    ONE<span className="text-[#2563EB]">X</span>
  </Link>
);

export const LangSwitch = ({ className = "" }) => {
  const { lang, setLang, t } = useI18n();
  return (
    <button data-testid="lang-switch-btn" onClick={() => setLang(lang === "ar" ? "en" : "ar")} className={`flex items-center gap-1.5 text-sm font-medium hover:text-[#2563EB] transition-colors ${className}`}>
      <Globe className="w-4 h-4" />{t("nav.switch_lang")}
    </button>
  );
};

export default function Header() {
  const { t } = useI18n();
  const { count, setOpen } = useCart();
  const { user, isStaff } = useAuth();
  const [q, setQ] = useState("");
  const [menu, setMenu] = useState(false);
  const nav = useNavigate();
  const submit = (e) => { e.preventDefault(); nav(`/products?q=${encodeURIComponent(q)}`); setMenu(false); };
  const links = [["/products", t("nav.products")], ["/products?sort=new", t("nav.new")], ["/products?offer=1", t("nav.offers")], ["/track", t("nav.track")], ["/support", t("nav.support")]];
  return (
    <header className="print:hidden sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-[#E5E7EB]">
      <div className="container-x h-16 flex items-center gap-4">
        <button data-testid="mobile-menu-btn" className="lg:hidden p-2 -ms-2" onClick={() => setMenu(!menu)} aria-label="menu">{menu ? <X /> : <Menu />}</button>
        <Logo />
        <nav className="hidden lg:flex items-center gap-6 ms-6">
          {links.map(([to, label]) => <NavLink key={to} to={to} className="text-sm font-medium text-[#111827] hover:text-[#2563EB] transition-colors">{label}</NavLink>)}
        </nav>
        <form onSubmit={submit} className="hidden md:flex flex-1 max-w-sm ms-auto relative">
          <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-4 text-[#6B7280]" />
          <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("nav.search_placeholder")} className="input h-10 ps-10 rounded-full bg-[#F5F7FA] border-transparent" />
        </form>
        <div className="flex items-center gap-1 ms-auto md:ms-2">
          <LangSwitch className="hidden sm:flex px-2" />
          {isStaff && <Link data-testid="header-admin-link" to="/admin" className="p-2.5 rounded-full hover:bg-[#F5F7FA]" title={t("nav.admin")}><LayoutDashboard className="w-5 h-5" /></Link>}
          <Link data-testid="header-account-link" to={user ? "/account" : "/login"} className="p-2.5 rounded-full hover:bg-[#F5F7FA]"><User className="w-5 h-5" /></Link>
          <button data-testid="header-cart-btn" onClick={() => setOpen(true)} className="relative p-2.5 rounded-full hover:bg-[#F5F7FA]">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && <span data-testid="header-cart-count" className="absolute -top-0.5 -end-0.5 min-w-5 h-5 px-1 rounded-full bg-[#2563EB] text-white text-[11px] font-bold flex items-center justify-center">{count}</span>}
          </button>
        </div>
      </div>
      {menu && (
        <div className="lg:hidden border-t border-[#E5E7EB] bg-white rise">
          <div className="container-x py-4 space-y-1">
            <form onSubmit={submit} className="relative mb-3">
              <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-4 text-[#6B7280]" />
              <input data-testid="mobile-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("nav.search_placeholder")} className="input ps-10 rounded-full bg-[#F5F7FA]" />
            </form>
            {links.map(([to, label]) => <Link key={to} to={to} onClick={() => setMenu(false)} className="block py-3 text-base font-medium border-b border-[#F3F4F6]">{label}</Link>)}
            <LangSwitch className="py-3" />
          </div>
        </div>
      )}
    </header>
  );
}
